import { COMMENT_MODE_CURSOR } from './styles.js';
import type { CaptureData, ElementMeta, ResolvedBugSmashConfig } from '../types.js';
import { capturePageSection } from '../capture.js';
import { fetchWidgetPins, submitReview } from '../api.js';
import { renderCommentBoxHtml, getSubmitIcon, setupCommentComposer, type CommentComposerHandle } from './commentComposer.js';
import { appendWidgetShell } from './widgetDom.js';
import { renderInitModalHtml, setupInitModal } from './initModal.js';
import {
  buildSelectorPath,
  getReviewerEmail,
  getReviewerName,
  replacePersistedPins,
  savePersistedPin,
  setReviewerName,
} from './widgetSession.js';

type WidgetState = 'idle' | 'awaiting-name' | 'comment-mode' | 'popover' | 'submitting' | 'success';

/**
 * Non-click events blocked during any active comment session so that page
 * links, buttons, forms and keyboard shortcuts cannot fire while the reviewer
 * is annotating. Scroll/wheel is intentionally omitted so reviewers can pan
 * the page before placing a pin. Click is handled separately by handleDocClick.
 *
 * All handlers are registered on `window` (not `document`) in the capture
 * phase. The capture path is Window → document → html → body → element, so a
 * window-level capture listener fires before ANY document- or element-level
 * listener, regardless of when those were registered. This defeats event
 * delegation frameworks (React, Vue, jQuery) that register on document/root.
 */
const BLOCKED_EVENTS: ReadonlyArray<string> = [
  'mousedown', 'mouseup', 'pointerdown', 'pointerup',
  'auxclick', 'dblclick', 'contextmenu', 'submit',
  'keydown', 'keyup', 'keypress',
];

interface PlacedPin {
  num: number;
  element: Element;
  relX: number;
  relY: number;
  pinEl: HTMLDivElement;
  submitted: boolean;
  elementMeta: ElementMeta;
  text: string;
}

export class Widget {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private config: ResolvedBugSmashConfig;
  private state: WidgetState = 'idle';
  private pins: PlacedPin[] = [];
  private nextPinNum = 1;
  private triggerBtn!: HTMLButtonElement;
  private triggerWrap!: HTMLDivElement;
  private viewportBorder!: HTMLDivElement;
  private highlight!: HTMLDivElement;
  private hint!: HTMLDivElement;
  private exitBtn!: HTMLButtonElement;
  private pinsContainer!: HTMLDivElement;
  private pinTooltip!: HTMLDivElement;
  private modalBackdrop!: HTMLDivElement;
  private commentBox!: HTMLDivElement;
  private nameOverlay!: HTMLDivElement;
  private nameModal!: HTMLDivElement;
  private successToast!: HTMLDivElement;
  private triggerTooltip!: HTMLDivElement;
  private commentComposer: CommentComposerHandle | null = null;
  private pendingCapture: CaptureData | null = null;
  private pendingCapturePromise: Promise<CaptureData | null> | null = null;
  private pendingCaptureX = 0;
  private pendingCaptureY = 0;
  private boundDocMouseMove: (e: MouseEvent) => void;
  private boundDocClick: (e: MouseEvent) => void;
  private boundKeyDown: (e: KeyboardEvent) => void;
  private boundBlockEvent: (e: Event) => void;
  private boundReflow: () => void;
  private boundTriggerMouseEnter: () => void;
  private boundTriggerMouseLeave: () => void;
  private rafId: number | null = null;
  private cursorStyleEl: HTMLStyleElement | null = null;
  private hoveredTarget: Element | null = null;

  constructor(config: ResolvedBugSmashConfig) {
    this.config = config;
    this.host = document.createElement('div');
    this.host.setAttribute('id', 'bugsmash-widget-root');
    this.shadow = this.host.attachShadow({ mode: 'open' });

    this.boundDocMouseMove = this.handleDocMouseMove.bind(this);
    this.boundDocClick    = this.handleDocClick.bind(this);
    this.boundKeyDown     = this.handleKeyDown.bind(this);
    this.boundBlockEvent  = this.handleBlockEvent.bind(this);
    this.boundReflow      = this.repositionAllPins.bind(this);
    this.boundTriggerMouseEnter = () => this.showDisabledTooltip();
    this.boundTriggerMouseLeave = () => this.hideDisabledTooltip();

    const dom = appendWidgetShell({
      shadow: this.shadow,
      position: this.config.position,
      onTriggerClick: () => this.onTriggerClick(),
      onExitClick: () => this.exitCommentMode(),
    });
    this.triggerBtn = dom.triggerBtn;
    this.triggerWrap = dom.triggerWrap;
    this.triggerTooltip = dom.triggerTooltip;
    this.viewportBorder = dom.viewportBorder;
    this.highlight = dom.highlight;
    this.hint = dom.hint;
    this.exitBtn = dom.exitBtn;
    this.pinsContainer = dom.pinsContainer;
    this.pinTooltip = dom.pinTooltip;
    this.modalBackdrop = dom.modalBackdrop;
    this.commentBox = dom.commentBox;
    this.nameOverlay = dom.nameOverlay;
    this.nameModal = dom.nameModal;
    this.successToast = dom.successToast;
    this.modalBackdrop.addEventListener('click', (event) => this.handleCommentBoxBackdropClick(event));
    document.body.appendChild(this.host);
    document.addEventListener('keydown', this.boundKeyDown);
    window.addEventListener('scroll', this.boundReflow, { passive: true });
    window.addEventListener('resize', this.boundReflow, { passive: true });
    if (this.config.commentsEnabled) {
      void this.restorePersistedPins();
    } else {
      this.disableComments();
    }
  }

  private disableComments() {
    this.triggerBtn.disabled = true;
    this.triggerBtn.classList.add('bs-trigger--disabled');
    this.triggerBtn.setAttribute('aria-label', 'Comments disabled');
    this.triggerWrap.addEventListener('mouseenter', this.boundTriggerMouseEnter);
    this.triggerWrap.addEventListener('mouseleave', this.boundTriggerMouseLeave);
  }

  private showDisabledTooltip() {
    if (this.config.commentsEnabled) return;
    this.triggerTooltip.hidden = false;
    this.triggerWrap.classList.add('bs-show-trigger-tooltip');
  }

  private hideDisabledTooltip() {
    this.triggerWrap.classList.remove('bs-show-trigger-tooltip');
    this.triggerTooltip.hidden = true;
  }

  private async restorePersistedPins() {
    const stored = await this.resolvePersistedPins();
    if (stored.length === 0) return;

    for (const p of stored) {
      const element = p.domPath ? document.querySelector(p.domPath) : null;

      let clientX: number;
      let clientY: number;

      if (element) {
        const rect = element.getBoundingClientRect();
        clientX = rect.left + rect.width  * (p.relX  ?? 0.5);
        clientY = rect.top  + rect.height * (p.relY  ?? 0.5);
      } else {
        clientX = p.cx * window.innerWidth;
        clientY = p.cy * window.innerHeight;
      }

      const pinEl = this.createPinEl(p.num, clientX, clientY, p.text, true);
      this.pinsContainer.appendChild(pinEl);
      this.nextPinNum = Math.max(this.nextPinNum, p.num + 1);

      if (element) {
        this.pins.push({
          num: p.num,
          element,
          relX: p.relX ?? 0.5,
          relY: p.relY ?? 0.5,
          pinEl,
          submitted: true,
          text: p.text,
          elementMeta: {
            domPath: p.domPath ?? '',
            tagName: element.tagName.toLowerCase(),
            elementWidth: p.elementWidth ?? 0,
            elementHeight: p.elementHeight ?? 0,
            textContent: '',
          },
        });
      }
    }
  }

  private async resolvePersistedPins() {
    try {
      const pins = await fetchWidgetPins(this.config, window.location.href);
      const persisted = pins.map((pin) => ({
        num: pin.num,
        cx: pin.cx,
        cy: pin.cy,
        text: pin.text,
        domPath: pin.domPath ?? '',
        relX: pin.relX ?? 0.5,
        relY: pin.relY ?? 0.5,
        elementWidth: pin.elementWidth ?? 0,
        elementHeight: pin.elementHeight ?? 0,
      }));
      replacePersistedPins(this.config.projectId, window.location.pathname, persisted);
      return persisted;
    } catch (error) {
      console.warn('[BugSmash] failed to refresh widget pins', error);
      return [];
    }
  }

  private onTriggerClick() {
    if (!this.config.commentsEnabled) return;
    if (this.state !== 'idle') { this.exitCommentMode(); return; }
    if (!getReviewerName(this.config.projectId)) {
      this.showInitModal();
    } else {
      this.enterCommentMode();
    }
  }

  private showInitModal() {
    this.state = 'awaiting-name';
    this.triggerBtn.classList.add('bs-active');
    this.triggerBtn.setAttribute('aria-label', 'Cancel feedback');

    this.nameModal.innerHTML = renderInitModalHtml();
    this.nameOverlay.style.display = 'block';
    this.nameModal.style.display = 'flex';

    setupInitModal({
      modal: this.nameModal,
      onClose: () => this.exitCommentMode(),
      onSubmit: (name) => this.completeInit(name),
    });
  }

  private completeInit(name: string) {
    setReviewerName(this.config.projectId, name);
    this.hideNameModal();
    this.enterCommentMode();
  }

  private hideNameModal() {
    this.nameOverlay.style.display = 'none';
    this.nameModal.style.display = 'none';
    this.nameModal.innerHTML = '';
  }

  private enterCommentMode() {
    this.state = 'comment-mode';
    this.triggerBtn.classList.add('bs-active');
    this.triggerBtn.setAttribute('aria-label', 'Exit comment mode');

    this.injectCursorStyle();

    this.viewportBorder.style.display = 'block';
    this.hint.style.display = 'block';
    this.exitBtn.style.display = 'flex';
    this.pinsContainer.style.display = 'block';

    this.repositionAllPins();
    this.startReflowLoop();

    document.addEventListener('mousemove', this.boundDocMouseMove);
    window.addEventListener('click', this.boundDocClick, true);
    this.addBlockListeners();
  }

  private exitCommentMode() {
    this.state = 'idle';
    this.pendingCapture = null;
    this.hoveredTarget = null;
    this.stopReflowLoop();
    this.removeDocListeners();
    this.removeBlockListeners();
    this.destroyCommentComposer();

    this.removeCursorStyle();
    this.viewportBorder.style.display = 'none';
    this.highlight.style.display = 'none';
    this.hint.style.display = 'none';
    this.exitBtn.style.display = 'none';
    this.modalBackdrop.style.display = 'none';
    this.commentBox.style.display = 'none';
    this.commentBox.innerHTML = '';
    this.hideNameModal();

    this.triggerBtn.classList.remove('bs-active');
    this.triggerBtn.setAttribute('aria-label', 'Leave feedback');

    this.pinsContainer.style.display = 'none';
    this.pinTooltip.style.display = 'none';
  }

  private handleDocMouseMove(e: MouseEvent) {
    if (this.state !== 'comment-mode') return;

    const target = e.target as Element;
    if (this.host.contains(target) || target === this.host) {
      this.hoveredTarget = null;
      this.highlight.style.display = 'none';
      return;
    }

    this.hoveredTarget = target;
    this.updateHighlight();
  }

  private updateHighlight() {
    if (!this.hoveredTarget) {
      this.highlight.style.display = 'none';
      return;
    }
    const rect = this.hoveredTarget.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      this.highlight.style.display = 'none';
      return;
    }
    this.highlight.style.display = 'block';
    this.highlight.style.top    = `${rect.top}px`;
    this.highlight.style.left   = `${rect.left}px`;
    this.highlight.style.width  = `${rect.width}px`;
    this.highlight.style.height = `${rect.height}px`;
  }

  private handleDocClick(e: MouseEvent) {
    if (this.state !== 'comment-mode') return;
    if (e.composedPath().includes(this.host)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    this.hoveredTarget = null;
    this.highlight.style.display = 'none';
    this.removeDocListeners();

    const target = e.target as Element;
    const rect = target.getBoundingClientRect();
    const relX = rect.width  > 0 ? (e.clientX - rect.left) / rect.width  : 0.5;
    const relY = rect.height > 0 ? (e.clientY - rect.top)  / rect.height : 0.5;

    const elementMeta: ElementMeta = {
      domPath:       buildSelectorPath(target),
      tagName:       target.tagName.toLowerCase(),
      elementWidth:  Math.round(rect.width),
      elementHeight: Math.round(rect.height),
      textContent:   (target.textContent ?? '').trim().slice(0, 200),
    };

    const optimisticNum = this.nextPinNum;
    const pinEl = this.createPinEl(optimisticNum, e.clientX, e.clientY);

    const pin: PlacedPin = {
      num: optimisticNum,
      element: target, relX, relY,
      pinEl,
      submitted: false,
      text: '',
      elementMeta,
    };
    this.pins.push(pin);
    this.pinsContainer.appendChild(pinEl);

    this.pendingCaptureX = e.clientX;
    this.pendingCaptureY = e.clientY;
    this.state = 'popover';
    this.pendingCapture = null;
    this.pendingCapturePromise = null;
    this.showCommentBox();

    this.pendingCapturePromise = capturePageSection(e.clientX, e.clientY)
      .then((data) => {
        this.pendingCapture = data;
        return data;
      })
      .catch((err) => {
        console.error('[BugSmash] capture failed', err);
        return null;
      });
  }

  private createPinEl(
    num: number,
    clientX: number,
    clientY: number,
    text = '',
    ghost = false,
  ): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'bs-pin';
    if (ghost) el.style.opacity = '0.65';
    el.style.left = `${clientX}px`;
    el.style.top  = `${clientY}px`;
    el.innerHTML  = `<span class="bs-pin-number">${num}</span>`;

    if (text) {
      el.addEventListener('mouseenter', () => {
        this.pinTooltip.textContent = text;
        this.pinTooltip.style.left    = el.style.left;
        this.pinTooltip.style.top     = el.style.top;
        this.pinTooltip.style.display = 'block';
      });
      el.addEventListener('mouseleave', () => {
        this.pinTooltip.style.display = 'none';
      });
    }

    return el;
  }

  private repositionAllPins() {
    for (const pin of this.pins) {
      const rect = pin.element.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        pin.pinEl.style.display = 'none';
        continue;
      }
      pin.pinEl.style.display = '';
      pin.pinEl.style.left = `${rect.left + rect.width  * pin.relX}px`;
      pin.pinEl.style.top  = `${rect.top  + rect.height * pin.relY}px`;
    }
    this.updateHighlight();
  }

  private showCommentBox() {
    this.removeCursorStyle();
    this.destroyCommentComposer();
    this.commentBox.innerHTML = renderCommentBoxHtml();
    this.modalBackdrop.style.display = 'block';
    this.commentBox.style.display = 'flex';
    this.hint.style.display = 'none';
    this.commentComposer = setupCommentComposer({
      container: this.commentBox,
      onSubmit: () => { void this.handleSubmit(); },
    });
    this.commentComposer.focus();
  }

  private handleCommentBoxBackdropClick(event: MouseEvent) {
    if (this.state !== 'popover') return;

    event.stopPropagation();
    if (!this.confirmDismissCommentBox()) return;
    this.dismissCommentBox();
  }

  private confirmDismissCommentBox() {
    if (!this.commentComposer || this.commentComposer.isEmpty()) return true;
    return window.confirm('Discard this comment? Your unsaved feedback will be lost.');
  }

  private dismissCommentBox() {
    if (this.state !== 'popover') return;

    this.destroyCommentComposer();
    this.modalBackdrop.style.display = 'none';
    this.commentBox.style.display = 'none';
    this.commentBox.innerHTML = '';
    this.pendingCapture = null;
    this.pendingCapturePromise = null;
    this.removeLastUnsubmittedPin();

    this.state = 'comment-mode';
    this.injectCursorStyle();
    this.hint.style.display = 'block';
    queueMicrotask(() => {
      document.addEventListener('mousemove', this.boundDocMouseMove);
      window.addEventListener('click', this.boundDocClick, true);
    });
  }

  private removeLastUnsubmittedPin() {
    const pin = this.pins.findLast((item) => !item.submitted);
    if (!pin) return;

    pin.pinEl.remove();
    this.pins = this.pins.filter((item) => item !== pin);
  }

  private async handleSubmit() {
    const text = this.commentComposer?.getHtml() ?? '';
    const plainText = this.commentComposer?.getPlainText() ?? '';
    if (!this.validateComment(text, plainText)) return;

    this.state = 'submitting';
    this.commentComposer?.stopVoiceInput();
    const submitBtn = this.getSubmitButton();
    this.setSubmitButtonLoading(submitBtn, true);

    try {
      const capture = await this.waitForCapture(8000);
      const result = await submitReview(this.config, this.buildSubmitPayload(text, capture));
      this.applySubmittedPin(result.number, plainText);
      this.showSubmitSuccess();
    } catch (err) {
      this.handleSubmitError(err, submitBtn);
    }
  }

  private validateComment(text: string, plainText: string) {
    if (text && plainText) return true;
    this.commentComposer?.setError('Please add a comment before submitting.');
    return false;
  }

  private getSubmitButton() {
    return this.commentBox.querySelector<HTMLButtonElement>('.bs-submit-btn');
  }

  private buildSubmitPayload(text: string, capture: CaptureData | null) {
    const pin = this.pins.findLast((item) => !item.submitted);

    return {
      text,
      feedbackByName: getReviewerName(this.config.projectId) ?? 'Anonymous',
      feedbackByEmail: getReviewerEmail(this.config.projectId) ?? undefined,
      clickX: this.pendingCaptureX,
      clickY: this.pendingCaptureY,
      relX: pin?.relX ?? 0.5,
      relY: pin?.relY ?? 0.5,
      url: window.location.href,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      elementMeta: pin?.elementMeta ?? this.emptyElementMeta(),
      capture,
      viewportScreenshot: null,
      attachment: this.commentComposer?.getAttachment() ?? null,
    };
  }

  private emptyElementMeta(): ElementMeta {
    return {
      domPath: '',
      tagName: 'unknown',
      elementWidth: 0,
      elementHeight: 0,
      textContent: '',
    };
  }

  private applySubmittedPin(number: number, plainText: string) {
    const pin = this.pins.findLast((item) => !item.submitted);
    if (pin) this.markPinSubmitted(pin, number, plainText);

    this.nextPinNum = number + 1;
    savePersistedPin(this.config.projectId, window.location.pathname, {
      num: number,
      cx: this.pendingCaptureX / window.innerWidth,
      cy: this.pendingCaptureY / window.innerHeight,
      text: plainText,
      domPath: pin?.elementMeta.domPath ?? '',
      relX: pin?.relX ?? 0.5,
      relY: pin?.relY ?? 0.5,
      elementWidth: pin?.elementMeta.elementWidth ?? 0,
      elementHeight: pin?.elementMeta.elementHeight ?? 0,
    });
  }

  private markPinSubmitted(pin: PlacedPin, number: number, plainText: string) {
    pin.submitted = true;
    pin.num = number;
    pin.text = plainText;
    pin.pinEl.querySelector('.bs-pin-number')!.textContent = String(number);
    pin.pinEl.addEventListener('mouseenter', () => this.showPinTooltip(pin.pinEl, plainText));
    pin.pinEl.addEventListener('mouseleave', () => { this.pinTooltip.style.display = 'none'; });
  }

  private showPinTooltip(pinEl: HTMLDivElement, plainText: string) {
    this.pinTooltip.textContent = plainText;
    this.pinTooltip.style.left = pinEl.style.left;
    this.pinTooltip.style.top = pinEl.style.top;
    this.pinTooltip.style.display = 'block';
  }

  private showSubmitSuccess() {
    this.state = 'success';
    this.destroyCommentComposer();
    this.modalBackdrop.style.display = 'none';
    this.commentBox.style.display = 'none';
    this.commentBox.innerHTML = '';
    this.successToast.style.display = 'block';
    this.successToast.classList.remove('is-visible');
    void this.successToast.offsetWidth;
    this.successToast.classList.add('is-visible');
    setTimeout(() => {
      this.successToast.style.display = 'none';
      this.successToast.classList.remove('is-visible');
      this.pendingCapture = null;
      this.pendingCapturePromise = null;
      void this.enterCommentMode();
    }, 500);
  }

  private handleSubmitError(error: unknown, submitBtn?: HTMLButtonElement | null) {
    this.state = 'popover';
    this.setSubmitButtonLoading(submitBtn, false);
    this.commentComposer?.setError('Submission failed. Please try again.');
    console.error('[BugSmash] submit error', error);
  }

  private setSubmitButtonLoading(
    submitBtn: HTMLButtonElement | null | undefined,
    loading: boolean,
  ) {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.innerHTML = loading ? this.loadingSubmitIcon() : this.defaultSubmitIcon();
  }

  private loadingSubmitIcon() {
    return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="animation:bs-spin 0.7s linear infinite"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>`;
  }

  private defaultSubmitIcon() {
    return getSubmitIcon();
  }

  private waitForCapture(ms: number): Promise<CaptureData | null> {
    if (this.pendingCapturePromise) {
      return this.withTimeout(this.pendingCapturePromise, ms);
    }

    return new Promise((resolve) => {
      if (this.pendingCapture) return resolve(this.pendingCapture);
      const deadline = Date.now() + ms;
      const t = setInterval(() => {
        if (this.pendingCapture) { clearInterval(t); resolve(this.pendingCapture); }
        else if (Date.now() > deadline) { clearInterval(t); resolve(null); }
      }, 100);
    });
  }

  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => resolve(null), ms);
      promise
        .then((value) => resolve(value))
        .catch(() => resolve(null))
        .finally(() => window.clearTimeout(timeoutId));
    });
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.state !== 'idle') this.exitCommentMode();
  }

  private removeDocListeners() {
    document.removeEventListener('mousemove', this.boundDocMouseMove);
    window.removeEventListener('click', this.boundDocClick, true);
  }

  private destroyCommentComposer() {
    this.commentComposer?.destroy();
    this.commentComposer = null;
  }

  /**
   * RAF loop that continuously repositions pins while comment mode is active.
   * This ensures pins track animated elements (carousels, marquees, sliders)
   * that move without triggering window scroll or resize events.
   */
  private startReflowLoop() {
    if (this.rafId !== null) return;
    const tick = () => {
      this.repositionAllPins();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stopReflowLoop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private handleBlockEvent(e: Event) {
    if (e.composedPath().includes(this.host)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }

  private addBlockListeners() {
    for (const type of BLOCKED_EVENTS) {
      window.addEventListener(type, this.boundBlockEvent, true);
    }
  }

  private removeBlockListeners() {
    for (const type of BLOCKED_EVENTS) {
      window.removeEventListener(type, this.boundBlockEvent, true);
    }
  }

  /**
   * Inject a <style> tag into the host page <head> that forces the comment
   * cursor on every element via !important. This overrides element-level
   * `cursor: pointer` styles on links, buttons, etc. which a body-level
   * inline style cannot beat.
   */
  private injectCursorStyle() {
    if (this.cursorStyleEl) return;
    const style = document.createElement('style');
    style.id = 'bs-comment-cursor';
    style.textContent = [
      `*, *::before, *::after { cursor: ${COMMENT_MODE_CURSOR} !important; }`,
      'input, textarea, [contenteditable] { cursor: text !important; }',
    ].join('\n');
    document.head.appendChild(style);
    this.cursorStyleEl = style;
  }

  private removeCursorStyle() {
    if (this.cursorStyleEl) {
      this.cursorStyleEl.remove();
      this.cursorStyleEl = null;
    }
  }

  destroy() {
    document.removeEventListener('keydown', this.boundKeyDown);
    window.removeEventListener('scroll', this.boundReflow);
    window.removeEventListener('resize', this.boundReflow);
    this.triggerWrap.removeEventListener('mouseenter', this.boundTriggerMouseEnter);
    this.triggerWrap.removeEventListener('mouseleave', this.boundTriggerMouseLeave);
    this.stopReflowLoop();
    this.removeDocListeners();
    this.removeBlockListeners();
    this.destroyCommentComposer();
    this.removeCursorStyle();
    this.host.remove();
  }
}
